import type { PropertyFilters } from '~/core/entities/property/filters';
import type {
	BoundariesCleared,
	BreadcrumbClicked,
	CardCollectionClicked,
	CardFeatureClicked,
	CardHeroOpened,
	CardNeighborhoodClicked,
	CarouselNeighborhoodClicked,
	CloseClicked,
	CondoCardClicked,
	CopiedCommercialId,
	CtaHomeClicked,
	CtaOffMarketClicked,
	CtaProfileClicked,
	CtaSimilarPropertiesClicked,
	CtaViewMoreClicked,
	EmptyState,
	EmptyStateFilter,
	FaqClicked,
	FilterApplied,
	FormClicked,
	FormCondoClicked,
	FormPropertyAuthentication,
	FormPropertyClicked,
	FormPropertyCompleted,
	FormPropertyError,
	FormPropertyInitiated,
	FormPropertyViewed,
	LinkClicked,
	LogoutCompleted,
	MapListingClicked,
	MapListingInteracted,
	MapPropertyOpened,
	MenuCollectionClicked,
	MenuCondoClicked,
	MenuPropertyClicked,
	ModalPhotoOpened,
	ModalVideoOpened,
	PageError,
	PageViewParams,
	ProfileUpdated,
	PropertyCardClicked,
	PropertyCardImageScrolled,
	PropertyFavorited,
	QuickNeighborhoodFilterApplied,
	RegionCardClicked,
	ReorderClicked,
	ReturnClicked,
	ReturnPropertyDetails,
	SearchBarClicked,
	SearchHistoryFilterClicked,
	SelectionCleared,
	ShareCollectionClicked,
	SharePropertyClicked,
	ShortsViewedEntry,
	ShortsViewedExit,
	TabNeighborhoodClicked
} from '~/types/mixpanel';

import { MixpanelEvents } from '~/core/service/mixpanel';
import { minMaxString, safeTrack } from '~~/utils';

import { useTrackHeader } from './headerEvents';
import { useTrackSearchPropertyEvents } from './useTrackSearchPropertyEvents';
import { useTrackSignAndLogin } from './useTrackSignAndLogin';

type BuildEmptyStateParams = {
	filters: PropertyFilters;
	currentPage: string;
	propertiesCount: number;
};

type ShortsFiltersForTracking = {
	cities?: string[];
	macroregions?: string[];
	regions?: string[];
	propertyTypes?: string[];
	minAskingPrice?: number;
	maxAskingPrice?: number;
	minArea?: number;
	maxArea?: number;
};

type BuildShortsEmptyStateParams = {
	filters: ShortsFiltersForTracking;
	currentPage: string;
	propertiesCount: number;
};

export const useEvents = () => {
	const buildEmptyStateParams = ({
		filters,
		currentPage,
		propertiesCount
	}: BuildEmptyStateParams): EmptyState => ({
		current_page: currentPage,
		empty_type: propertiesCount === 0 ? '0 results' : 'few results',
		properties_count: propertiesCount,
		click_square: false,
		filter_city: filters.cities?.join(', ') || '',
		filter_region: filters.macroregions?.join(', ') || '',
		filter_neighborhood: filters.regions?.join(', ') || '',
		filter_area: minMaxString(filters.minArea, filters.maxArea),
		filter_iptu: minMaxString(filters.minTax, filters.maxTax),
		filter_price: minMaxString(filters.minAskingPrice, filters.maxAskingPrice),
		filter_typology: filters.propertyTypes?.join(', ') || '',
		filter_bedrooms: filters.bedrooms || 0,
		filter_bathrooms: filters.bathrooms || 0,
		filter_garage: filters.parkingSpots || 0,
		filter_suites: filters.suites || 0,
		filter_attributes: filters.features?.join(', ') || '',
		filter_exclusives: filters.isExclusive || false
	});

	const buildShortsEmptyStateParams = ({
		filters,
		currentPage,
		propertiesCount
	}: BuildShortsEmptyStateParams) => ({
		current_page: currentPage,
		empty_type: propertiesCount === 0 ? '0 results' : 'few results',
		filter_city: filters.cities?.join(', ') || '',
		filter_region: filters.macroregions?.join(', ') || '',
		filter_neighborhood: filters.regions?.join(', ') || '',
		filter_area: minMaxString(filters.minArea, filters.maxArea),
		filter_price: minMaxString(filters.minAskingPrice, filters.maxAskingPrice),
		filter_typology: filters.propertyTypes?.join(', ') || ''
	});

	return {
		trackPageView: ({
			event_name = MixpanelEvents.PAGE_VIEWED,
			...params
		}: PageViewParams) => {
			safeTrack(MixpanelEvents.PAGE_VIEWED, {
				event_name,
				...params
			});
		},
		trackFormBrokerClicked: (params: FormClicked) => {
			safeTrack(MixpanelEvents.FORM_BROKER_CLICKED, params);
		},
		trackFormOffMarketClicked: (params: FormClicked) => {
			safeTrack(MixpanelEvents.FORM_OFF_MARKET_CLICKED, params);
		},
		trackFormPropertyViewed: (params: FormPropertyViewed) => {
			safeTrack(MixpanelEvents.FORM_PROPERTY_VIEWED, params);
		},
		trackFormPropertyClicked: (params: FormPropertyClicked) => {
			safeTrack(MixpanelEvents.FORM_PROPERTY_CLICKED, params);
		},
		trackFormPropertyInitiated: (params: FormPropertyInitiated) => {
			safeTrack(MixpanelEvents.FORM_PROPERTY_INITIATED, params);
		},
		trackFormCondoClicked: (params: FormCondoClicked) => {
			safeTrack(MixpanelEvents.FORM_PROPERTY_CLICKED, params);
		},
		trackCtaViewMoreClicked: (params: CtaViewMoreClicked) => {
			safeTrack(MixpanelEvents.CTA_VIEW_MORE_CLICKED, params);
		},
		trackFormSellingClicked: (params: FormClicked) => {
			safeTrack(MixpanelEvents.FORM_SELLING_CLICKED, params);
		},
		trackFaqClicked: (params: FaqClicked) => {
			safeTrack(MixpanelEvents.FAQ_CLICKED, params);
		},
		trackBreadcrumbClicked: (params: BreadcrumbClicked) => {
			safeTrack(MixpanelEvents.BREADCRUMB_CLICKED, params);
		},
		trackCardNeighborhoodClicked: (params: CardNeighborhoodClicked) => {
			safeTrack(MixpanelEvents.CARD_NEIGHBORHOOD_CLICKED, params);
		},
		trackMapListingClicked: (params: MapListingClicked) => {
			safeTrack(MixpanelEvents.MAP_LISTING_CLICKED, params);
		},
		trackMapListingInteracted: (params: MapListingInteracted) => {
			safeTrack(MixpanelEvents.MAP_LISTING_INTERACTED, params);
		},
		trackReturn: (params: ReturnClicked) => {
			safeTrack(MixpanelEvents.RETURN_CLICKED, params);
		},
		trackPropertyCardClicked: (params: PropertyCardClicked) => {
			safeTrack(MixpanelEvents.CARD_CLICKED, params);
		},
		trackCondoCardClicked: (params: CondoCardClicked) => {
			safeTrack(MixpanelEvents.CONDO_CARD_CLICKED, params);
		},
		trackPropertyImageScrolled: (params: PropertyCardImageScrolled) => {
			safeTrack(MixpanelEvents.CARD_IMAGE_SCROLLED, params);
		},
		trackFormPropertyAuthentication: (params: FormPropertyAuthentication) => {
			safeTrack(MixpanelEvents.FORM_PROPERTY_AUTHENTICATION, params);
		},
		trackFormPropertyCompleted: (params: FormPropertyCompleted) => {
			safeTrack(MixpanelEvents.FORM_PROPERTY_COMPLETED, params);
		},
		trackFormPropertyError: (params: FormPropertyError) => {
			safeTrack(MixpanelEvents.FORM_PROPERTY_ERROR, params);
		},
		trackingCloseClicked: (params: CloseClicked) => {
			safeTrack(MixpanelEvents.CLOSE_CLICKED, params);
		},
		trackingGoToPrevious: (params: CloseClicked) => {
			safeTrack(MixpanelEvents.RETURN_CLICKED, params);
		},
		trackReorderClicked: (params: ReorderClicked) => {
			safeTrack(MixpanelEvents.REORDER_CLICKED, params);
		},
		trackMenuPropertyClicked: (
			params: MenuPropertyClicked = {
				click_overview: false,
				click_attributes: false,
				click_service: false,
				click_location: false,
				click_similar_properties: false
			}
		) => {
			safeTrack(MixpanelEvents.MENU_PROPERTY_CLICKED, params);
		},
		trackMenuCondoClicked: (params: MenuCondoClicked) => {
			safeTrack(MixpanelEvents.MENU_CONDO_CLICKED, params);
		},
		trackMenuCollectionClicked: (params: MenuCollectionClicked) => {
			safeTrack(MixpanelEvents.MENU_COLLECTION_CLICKED, params);
		},
		trackSharePropertyClicked: (params: SharePropertyClicked) => {
			safeTrack(MixpanelEvents.SHARE_PROPERTY_CLICKED, params);
		},
		trackEmptyState: (params: BuildEmptyStateParams) => {
			const emptyStateParams = buildEmptyStateParams(params);

			safeTrack(MixpanelEvents.EMPTY_STATE, emptyStateParams);
		},
		trackShortsEmptyState: (params: BuildShortsEmptyStateParams) => {
			safeTrack(
				MixpanelEvents.EMPTY_STATE,
				buildShortsEmptyStateParams(params)
			);
		},
		trackLinkClicked: (params: LinkClicked) => {
			safeTrack(MixpanelEvents.LINK_CLICKED, params);
		},
		trackCardFeatureClicked: (params: CardFeatureClicked) => {
			safeTrack(MixpanelEvents.CARD_FEATURE_CLICKED, params);
		},
		trackRegionCardClicked: (params: RegionCardClicked) => {
			safeTrack(MixpanelEvents.REGION_CARD_CLICKED, params);
		},
		trackFilterApplied: (params: FilterApplied) => {
			safeTrack(MixpanelEvents.FILTER_APPLIED, params);
		},
		trackQuickNeighborhoodFilterApplied: (
			params: QuickNeighborhoodFilterApplied
		) => {
			safeTrack(MixpanelEvents.FILTER_APPLIED, params);
		},
		trackEmptyStateFilter: (params: EmptyStateFilter) => {
			safeTrack(MixpanelEvents.EMPTY_STATE_FILTER, params);
		},
		trackCardHeroOpened: (params: CardHeroOpened) => {
			safeTrack(MixpanelEvents.CARD_HERO_OPENED, params);
		},
		trackCtaOffMarketClicked: (params: CtaOffMarketClicked) => {
			safeTrack(MixpanelEvents.CTA_OFF_MARKET_CLICKED, params);
		},
		trackCarouselNeighborhoodClicked: (params: CarouselNeighborhoodClicked) => {
			safeTrack(MixpanelEvents.CAROUSEL_NEIGHBORHOOD_CLICKED, params);
		},
		trackSignAndLogin: useTrackSignAndLogin(),
		trackHeader: useTrackHeader(),
		searchPropertyEvents: useTrackSearchPropertyEvents(),
		trackModalPhotoOpened: (params: ModalPhotoOpened) => {
			safeTrack(MixpanelEvents.MODAL_PHOTO_OPENED, params);
		},
		trackModalVideoOpened: (params: ModalVideoOpened) => {
			safeTrack(MixpanelEvents.MODAL_VIDEO_OPENED, params);
		},
		trackMapPropertyOpened: (params: MapPropertyOpened) => {
			safeTrack(MixpanelEvents.MAP_PROPERTY_OPENED, params);
		},
		trackCtaHomeClicked: (params: CtaHomeClicked) => {
			safeTrack(MixpanelEvents.CTA_HOME_CLICKED, params);
		},
		trackCardCollectionClicked: (params: CardCollectionClicked) => {
			safeTrack(MixpanelEvents.CARD_COLLECTION_CLICKED, params);
		},
		trackPageError: (params: PageError) => {
			safeTrack(MixpanelEvents.PAGE_ERROR, params);
		},
		trackCookiesAccepted: () => {
			safeTrack(MixpanelEvents.COOKIES_ACCEPTED);
		},
		trackTabNeighborhoodClicked: (params: TabNeighborhoodClicked) => {
			safeTrack(MixpanelEvents.TAB_NEIGHBORHOOD_CLICKED, params);
		},
		trackShareCondoClicked: (params: SharePropertyClicked) => {
			safeTrack(MixpanelEvents.SHARE_CONDO_CLICKED, params);
		},
		trackSelectionCleared: (params: SelectionCleared) => {
			safeTrack(MixpanelEvents.SELECTION_CLEARED, params);
		},
		trackBoundariesCleared: (params: BoundariesCleared) => {
			safeTrack(MixpanelEvents.BOUNDARIES_CLEARED, params);
		},
		trackReturnPropertyDetails: (params: ReturnPropertyDetails) => {
			safeTrack(MixpanelEvents.RETURN_CLICKED, params);
		},
		trackSearchBarClicked: (params: SearchBarClicked) => {
			safeTrack(MixpanelEvents.SEARCH_BAR_CLICKED, params);
		},
		trackCopiedCommercialId: (params: CopiedCommercialId) => {
			safeTrack(MixpanelEvents.COMMERCIAL_ID_COPIED, params);
		},
		trackCtaSimilarPropertiesClicked: (params: CtaSimilarPropertiesClicked) => {
			safeTrack(MixpanelEvents.CTA_SIMILAR_PROPERTIES_CLICKED, params);
		},
		trackPropertyFavorited: (params: PropertyFavorited) => {
			safeTrack(MixpanelEvents.PROPERTY_FAVORITED, params);
		},
		trackShareCollectionClicked: (params: ShareCollectionClicked) => {
			safeTrack(MixpanelEvents.SHARE_COLLECTION_CLICKED, params);
		},
		trackLogoutCompleted: (params: LogoutCompleted) => {
			safeTrack(MixpanelEvents.LOGOUT_COMPLETED, params);
		},
		trackCtaProfileClicked: (params: CtaProfileClicked) => {
			safeTrack(MixpanelEvents.CTA_PROFILE_CLICKED, params);
		},
		trackProfileUpdated: (params: ProfileUpdated) => {
			safeTrack(MixpanelEvents.PROFILE_UPDATED, params);
		},
		trackSearchHistoryFilter: (params: SearchHistoryFilterClicked) => {
			safeTrack(MixpanelEvents.SEARCH_HISTORY_FILTER, params);
		},
		trackShortsViewedEntry: (params: ShortsViewedEntry) => {
			safeTrack(MixpanelEvents.SHORTS_VIEWED_ENTRY, params);
		},
		trackShortsViewedExit: (params: ShortsViewedExit) => {
			safeTrack(MixpanelEvents.SHORTS_VIEWED_EXIT, params);
		}
	};
};
